import { useCallback, useEffect, useMemo, useState } from 'react';
import { useAppData, useAppResource } from '../../context/AppDataContext';
import { teamService } from '../../services/teamService';
import { userService } from '../../services/userService';
import { getRoleLabel } from '../../utils/permissions';
import DataGrid, { actionsColumn, dash } from '../../components/DataGrid';
import './Teams.css';
import { PageLoader } from '../../components/Loader';

const LEADER_ROLE_NAMES = ['super_admin', 'admin'];

function isEligibleLeader(user) {
  if (!user) {
    return false;
  }

  if (LEADER_ROLE_NAMES.includes(user.role_name)) {
    return true;
  }

  if (user.role_name === 'auditor' && user.is_manager) {
    return true;
  }

  return false;
}

function Teams({ embedded = false }) {
  const { invalidateResourcesByPrefix } = useAppData();
  const loadTeams = useCallback(() => teamService.getAll(), []);
  const loadUsers = useCallback(() => userService.getAll(), []);
  const {
    data: teams = [],
    loading: teamsLoading,
    error: teamsError,
    refresh: refreshTeams,
  } = useAppResource('teams', loadTeams, []);
  const {
    data: allUsers = [],
    loading: usersLoading,
    error: usersError,
  } = useAppResource('users', loadUsers, []);
  const [error, setError] = useState('');

  const [selectedTeamId, setSelectedTeamId] = useState('');
  const [isCreatingTeam, setIsCreatingTeam] = useState(false);
  const [teamForm, setTeamForm] = useState({
    name: '',
    description: '',
  });
  const [teamMembers, setTeamMembers] = useState([]);
  const [leaderId, setLeaderId] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (teams.length > 0 && !selectedTeamId && !isCreatingTeam) {
      setSelectedTeamId(String(teams[0].id));
    }
  }, [teams, selectedTeamId, isCreatingTeam]);

  const loading = teamsLoading || usersLoading;
  const visibleError = error || teamsError || usersError;

  const selectedTeam = useMemo(
    () =>
      teams.find((team) => String(team.id) === String(selectedTeamId)) || null,
    [teams, selectedTeamId],
  );

  const loadTeamDetails = useCallback(
    async (teamId) => {
      if (!teamId) {
        setTeamForm({ name: '', description: '' });
        setTeamMembers([]);
        setLeaderId('');
        return;
      }

      setError('');
      try {
        const [teamData, membersData] = await Promise.all([
          teamService.getById(teamId),
          teamService.getMembers(teamId),
        ]);

        setTeamForm({
          name: teamData.name || '',
          description: teamData.description || '',
        });

        const sortedMembers = Array.isArray(membersData)
          ? [...membersData].sort((a, b) => {
              const orderA = a.sort_order ?? a.order ?? 0;
              const orderB = b.sort_order ?? b.order ?? 0;
              return orderA - orderB;
            })
          : [];
        setTeamMembers(sortedMembers);

        const existingLeader =
          teamData.leader_id ||
          (sortedMembers.find((member) => member.is_leader) || {}).id;
        setLeaderId(existingLeader ? String(existingLeader) : '');
      } catch (err) {
        setError(err.message);
      }
    },
    [],
  );

  useEffect(() => {
    if (selectedTeamId) {
      loadTeamDetails(selectedTeamId);
    }
  }, [selectedTeamId, loadTeamDetails]);

  const availableUsers = useMemo(() => {
    const memberIds = new Set(teamMembers.map((member) => member.id));
    // Clients view audits only; they are never team members.
    return allUsers.filter((user) => user.role_name !== 'client' && !memberIds.has(user.id));
  }, [allUsers, teamMembers]);

  const eligibleLeaders = useMemo(
    () => teamMembers.filter((member) => isEligibleLeader(member)),
    [teamMembers],
  );

  useEffect(() => {
    if (eligibleLeaders.length === 1) {
      setLeaderId(String(eligibleLeaders[0].id));
    }
  }, [eligibleLeaders]);

  const handleTeamFormChange = (event) => {
    const { name, value } = event.target;
    setTeamForm((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleLeaderChange = (event) => {
    setLeaderId(event.target.value);
  };

  const addMember = (user) => {
    setTeamMembers((prev) => {
      if (prev.some((member) => member.id === user.id)) {
        return prev;
      }
      return [...prev, user];
    });
  };

  const removeMember = (user) => {
    setTeamMembers((prev) => prev.filter((member) => member.id !== user.id));
    setLeaderId((current) =>
      current === String(user.id) ? '' : current,
    );
  };

  const onDragStartMember = (event, memberId) => {
    event.dataTransfer.setData('text/plain', String(memberId));
  };

  const onDragOverMember = (event) => {
    event.preventDefault();
  };

  const onDropMember = (event, targetId) => {
    event.preventDefault();
    const draggedId = event.dataTransfer.getData('text/plain');
    if (!draggedId || draggedId === String(targetId)) {
      return;
    }

    setTeamMembers((prev) => {
      const fromIndex = prev.findIndex(
        (member) => String(member.id) === String(draggedId),
      );
      const toIndex = prev.findIndex(
        (member) => String(member.id) === String(targetId),
      );
      if (fromIndex === -1 || toIndex === -1) {
        return prev;
      }

      const next = [...prev];
      const [moved] = next.splice(fromIndex, 1);
      next.splice(toIndex, 0, moved);
      return next;
    });
  };

  const handleSaveTeam = async () => {
    if (!teamForm.name.trim()) {
      setError('Team name is required.');
      return;
    }

    if (eligibleLeaders.length > 0 && !leaderId) {
      setError('Please select a team leader from eligible members.');
      return;
    }

    setSaving(true);
    setError('');

    try {
      let teamId = isCreatingTeam ? null : selectedTeam?.id || null;

      const payload = {
        name: teamForm.name.trim(),
        description: teamForm.description.trim() || null,
        leader_id: leaderId ? Number(leaderId) : null,
      };

      if (teamId) {
        await teamService.update(teamId, payload);
      } else {
        const created = await teamService.create(payload);
        teamId = created.id;
        setSelectedTeamId(String(teamId));
        setIsCreatingTeam(false);
      }

      const membersPayload = {
        member_ids: teamMembers.map((member) => member.id),
        leader_id: leaderId ? Number(leaderId) : null,
        order: teamMembers.map((member, index) => ({
          id: member.id,
          sort_order: index,
        })),
      };

      await teamService.updateMembers(teamId, membersPayload);

      invalidateResourcesByPrefix('audits:');
      await refreshTeams();
      if (teamId) {
        await loadTeamDetails(teamId);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const openTeam = (team) => {
    setIsCreatingTeam(false);
    setSelectedTeamId(String(team.id));
    setTimeout(() => {
      document.querySelector('.teams-builder')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 0);
  };

  const memberNames = (team) =>
    Array.isArray(team.members)
      ? team.members.map((member) => member.name || member.email).join(', ')
      : '';

  const teamColumns = [
    {
      headerName: 'Team',
      colId: 'name',
      valueGetter: (p) => p.data.name || 'Untitled team',
      minWidth: 170,
    },
    { headerName: 'Description', field: 'description', valueFormatter: (p) => dash(p.value), minWidth: 180 },
    {
      headerName: 'Members',
      colId: 'member_count',
      valueGetter: (p) => (Array.isArray(p.data.members) ? p.data.members.length : 0),
      filter: 'agNumberColumnFilter',
      maxWidth: 130,
    },
    {
      headerName: 'Member names',
      colId: 'member_names',
      valueGetter: (p) => memberNames(p.data),
      valueFormatter: (p) => dash(p.value),
      minWidth: 260,
      flex: 2,
      wrapText: true,
      autoHeight: true,
    },
    actionsColumn(
      (team) => (
        <button type="button" className="teams-page__add-btn teams-grid__open" onClick={() => openTeam(team)}>
          {String(team.id) === String(selectedTeamId) ? 'Editing' : 'Edit team'}
        </button>
      ),
      { width: 140 },
    ),
  ];

  return (
    <div className={embedded ? 'teams-page teams-page--embedded' : 'page teams-page'}>
      <header
        className={embedded ? 'teams-page__header teams-page__toolbar' : 'page__header teams-page__header'}
      >
        <div>
          {embedded ? (
            <h2>Teams</h2>
          ) : (
            <>
              <p className="page__eyebrow">Operations</p>
              <h1>Teams</h1>
            </>
          )}
          <p className="teams-page__subtitle">
            Build outlet and schedule teams with a clear leader.
          </p>
        </div>

        <div className="teams-page__actions">
          <button
            type="button"
            className="teams-page__add-btn"
            onClick={() => {
              setIsCreatingTeam(true);
              setSelectedTeamId('');
              setTeamForm({ name: '', description: '' });
              setTeamMembers([]);
              setLeaderId('');
              setError('');
            }}
          >
            + New Team
          </button>
        </div>
      </header>

      {visibleError && (
        <div className="teams-page__alert" role="alert">
          {visibleError}
        </div>
      )}

      <section className="teams-page__layout teams-page__layout--stacked">
        <div className="teams-grid">
          {loading ? (
            <PageLoader label="Loading teams…" compact />
          ) : teams.length === 0 ? (
            <p className="teams-sidebar__empty">
              No teams yet. Create your first team below.
            </p>
          ) : (
            <DataGrid
              rowData={teams}
              columnDefs={teamColumns}
              searchPlaceholder="Search team or member…"
              pageSize={10}
              gridProps={{
                getRowClass: (params) =>
                  String(params.data?.id) === String(selectedTeamId) ? 'data-grid__row--selected' : '',
                onRowClicked: (params) => openTeam(params.data),
              }}
            />
          )}
        </div>

        <div className="teams-builder">
          <div className="teams-builder__header">
            <div className="teams-builder__field">
              <label>
                Team name
                <input
                  type="text"
                  name="name"
                  value={teamForm.name}
                  onChange={handleTeamFormChange}
                  placeholder="e.g. North Zone Schedule Squad"
                />
              </label>
            </div>
            <div className="teams-builder__field">
              <label>
                Description
                <input
                  type="text"
                  name="description"
                  value={teamForm.description}
                  onChange={handleTeamFormChange}
                  placeholder="Optional summary of this team"
                />
              </label>
            </div>
          </div>

          <div className="teams-builder__body">
            <section className="teams-column teams-column--left">
              <header>
                <h3>Available users</h3>
                <p>{availableUsers.length} users</p>
              </header>
              <div className="teams-column__list">
                {availableUsers.length === 0 ? (
                  <p className="teams-column__empty">
                    Everyone is already in this team.
                  </p>
                ) : (
                  <ul>
                    {availableUsers.map((user) => (
                      <li key={user.id}>
                        <button
                          type="button"
                          onClick={() => addMember(user)}
                        >
                          <span className="teams-column__name">
                            {user.name || user.email}
                          </span>
                          <span className="teams-column__meta">
                            {user.email} · {getRoleLabel(user)}
                          </span>
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </section>

            <section className="teams-column teams-column--right">
              <header>
                <div>
                  <h3>Team members</h3>
                  <p>
                    Drag to reorder. One eligible member must be the leader.
                  </p>
                </div>
                <div className="teams-leader-select">
                  <label>
                    Leader
                    <select
                      value={leaderId}
                      onChange={handleLeaderChange}
                      disabled={eligibleLeaders.length === 0}
                    >
                      <option value="">
                        {eligibleLeaders.length === 0
                          ? 'No eligible leaders yet'
                          : 'Select leader'}
                      </option>
                      {eligibleLeaders.map((member) => (
                        <option key={member.id} value={member.id}>
                          {member.name || member.email}
                        </option>
                      ))}
                    </select>
                  </label>
                </div>
              </header>

              <div className="teams-column__list">
                {teamMembers.length === 0 ? (
                  <p className="teams-column__empty">
                    Add users from the left to build your team.
                  </p>
                ) : (
                  <ul>
                    {teamMembers.map((member) => (
                      <li
                        key={member.id}
                        draggable
                        onDragStart={(event) =>
                          onDragStartMember(event, member.id)
                        }
                        onDragOver={onDragOverMember}
                        onDrop={(event) =>
                          onDropMember(event, member.id)
                        }
                      >
                        <div className="teams-member-card">
                          <div className="teams-member-card__info">
                            <span className="teams-column__name">
                              {member.name || member.email}
                            </span>
                            <span className="teams-column__meta">
                              {member.email} ·{' '}
                              {getRoleLabel(allUsers.find((u) => String(u.id) === String(member.id)) || member)}
                            </span>
                          </div>
                          <div className="teams-member-card__badges">
                            {isEligibleLeader(member) && (
                              <span
                                className={`teams-badge ${
                                  String(member.id) === String(leaderId)
                                    ? 'teams-badge--primary'
                                    : ''
                                }`}
                              >
                                {String(member.id) === String(leaderId)
                                  ? 'Leader'
                                  : 'Eligible leader'}
                              </span>
                            )}
                            <button
                              type="button"
                              className="teams-member-card__remove"
                              onClick={() => removeMember(member)}
                            >
                              Remove
                            </button>
                          </div>
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </section>
          </div>

          <footer className="teams-builder__footer">
            <button
              type="button"
              className="teams-builder__save"
              disabled={saving}
              onClick={handleSaveTeam}
            >
              {saving ? 'Saving...' : 'Save team'}
            </button>
          </footer>
        </div>
      </section>
    </div>
  );
}

export default Teams;
